import React, { useState } from 'react';
import { WizardStepProps } from '../types/wizard.types';
import { ChooseListInput } from '@/shared/components/inputs/ChooseList.input';
import { cn } from '@/shared/lib/utils';
import { ComponentReturnTypeMap } from '@momentum/types';

interface ChoiceOption {
  id: string;
  title: string;
  description?: string;
  icon?: string;
}

interface Choice {
  question?: string; // Optional - not shown for single questions
  options: ChoiceOption[];
}

interface WizardChatbotChoiceProps
  extends WizardStepProps<ComponentReturnTypeMap['ChatbotChoice']> {
  choices: Choice[]; // Always an array, even for single choice
  showSkipOption?: boolean;
  skipText?: string;
}

export const WizardChatbotChoice: React.FC<WizardChatbotChoiceProps> = ({
  value,
  onNext,
  choices,
  showSkipOption = false,
  skipText = "I don't know",
}) => {
  const [answers, setAnswers] = useState<Record<number, string>>(value || {});
  // Handler for any choice change
  const handleChoiceChange = (choiceIndex: number) => (selectedId: string) => {
    const currentChoiceTitle = choices[choiceIndex]?.options.find(
      (opt) => opt.id === selectedId
    )?.title;
    const newAnswers = {
      ...answers,
      [choiceIndex]: currentChoiceTitle || selectedId,
    };

    setAnswers(newAnswers);

    // Check if all questions are answered
    const allAnswered = choices.every((_, index) => {
      const answer = newAnswers[index];
      return !!answer;
    });

    // If all answered, submit
    if (allAnswered) {
      if (choices.length === 1) {
        onNext(newAnswers); // Single choice - submit just the value
      } else {
        onNext(newAnswers); // Multiple questions - submit the object
      }
    }
  };

  const handleSkip = () => {
    onNext({});
  };

  return (
    <div className="space-y-6">
      {choices.map((choice, index) => {
        // Check if previous questions are answered
        const isPreviousAnswered = index === 0 || !!answers[index - 1];
        const isDisabled = !isPreviousAnswered;
        const currentAnswer = choice.options.find(
          (opt) => opt.title === answers[index]
        )?.id;

        return (
          <div
            key={index}
            className={cn(
              'space-y-3 transition-all duration-300',
              isDisabled && 'opacity-40 pointer-events-none scale-98'
            )}
          >
            {/* Show question text if provided - modern typography */}
            {choice.question && (
              <div className="text-[15px] font-semibold text-gray-800 tracking-tight">
                {choice.question}
              </div>
            )}

            <ChooseListInput
              options={choice.options}
              value={currentAnswer || ''}
              onChange={handleChoiceChange(index)}
              variant="chatbot"
              disabled={isDisabled}
            />
          </div>
        );
      })}

      {showSkipOption && (
        <button
          onClick={handleSkip}
          className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-150"
        >
          {skipText}
        </button>
      )}
    </div>
  );
};
